import { useMutation, useQueryClient } from '@tanstack/react-query';

import { joinStandbyMutation } from '@/api/standby/joinStandbyMutation';
import { useCustomerQuery } from '@/hooks/queries/customer/useCustomerQuery';

import type { UseJoinStandbyMutationOptions } from './types';

// The options go THROUGH the factory: spreading them over its result would
// let a caller re-enable the retry that keeps a network hiccup from
// double-creating the entry.
export const useJoinStandbyMutation = (
  options?: UseJoinStandbyMutationOptions,
) => {
  const queryClient = useQueryClient();
  const { customerId } = useCustomerQuery();

  return useMutation({
    ...joinStandbyMutation(options),
    onSuccess: (data, variables, onMutateResult, context) => {
      // A screen still mounted below this one in the stack (e.g. the
      // stylist/service screens the customer navigated through) holds its
      // own `useStandbyActiveEntryGuard` observer on this exact key — left
      // untouched, it keeps reporting "no active entry" until a fresh mount,
      // so a back-gesture could resurface the funnel post-join with only the
      // join-time 409 as a backstop.
      if (customerId) {
        queryClient.setQueryData(
          ['standby', 'activeEntry', variables.shopId, customerId],
          { entryId: data.entryId, statusUrl: data.statusUrl },
        );
      }
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
